import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { tap } from 'rxjs/operators';
import { ASYNC_METRIC_SWITCH_OPTIONS } from '../../../model/metric';
import { MetricService } from './metric.service';

// Limit on the number of simultaneous requests to avoid concurrency
// issues on the API (3 seems like a good balance between frontend
// performance and not overloading the backend)
const CONCURRENT_REQUEST_LIMIT = 3;

// When using the computationally-expensive async metrics, we should
// decrease the number of concurrent requests to avoid overwhelming
// our servers
const ASYNC_CONCURRENT_REQUEST_LIMIT = 1;

// How many times a request can be retried (retrying requests allows
// us to handle errors when we reach the backend's concurrency limit)
const DEFAULT_RETRIES_ALLOWED = 2;

// If the backend's concurrency limit is exceeded, we'll receive a 500 error
// from the API gateway (unfortunately, 500 is used for multiple errors and
// it doesn't look like this can be configured easily)
const ERROR_CODE_EXCEEDED_CONCURRENCY = 500;

@Injectable({
  providedIn: 'root'
})
export class QueueService {
  // this typing is on purpose. This queue can execute any observable
  private queue: Array<{ task: Observable<any>; name?: string; retriesRemaining: number }> = [];

  // Number of requests currently being processed by the backend
  // (should not be allowed to exceed CONCURRENT_REQUEST_LIMIT)
  private activeRequests = 0;

  private readonly debug = false;

  constructor(private readonly metricService: MetricService) {}

  /**
   * Add the given task to the queue, optionally replacing any tasks with the same name that are already queued
   *
   * @param task Observable<T> An observable to be executed when time comes. The queue will subscribe to this and forward the result
   * @param name string: A name for this task - if provided this task can be identified and for example replace by newer ones
   * @param replaceOld boolean: Replace all older tasks with the same name. Requires name to be provided as well
   *
   * @return Observable<T> The observable to subscribe to in the CALLER in order to be notified about task completion.
   */
  public add<T>(task: Observable<T>, name?: string, replaceOld: boolean = false): Observable<T> {
    return this.addWithSubscription(task, new Subject<T>(), DEFAULT_RETRIES_ALLOWED, name, replaceOld);
  }

  /**
   * Internal function that handles the actual logic of enqueuing the task
   * Sets up a Subject (so that we can retry the task if we get a possible concurrency error)
   * Tasks that fail with a 500 will be retried up to the given limit of attempts
   *
   * @param task Observable<T> An observable to be executed when time comes. The queue will subscribe to this and forward the result
   * @param sub Subject: Subject that will be notified when the task's observable is returning
   * @param retriesRemaining number: How many more times this request can be re-sent in case a 500 error is returned
   * @param name string: A name for this task - if provided this task can be identified and for example replace by newer ones
   * @param replaceOld boolean: Replace all older tasks with the same name. Requires name to be provided as wenn
   *
   * @return Observable<T> The observable to subscribe to in the CALLER in order to be notified about task completion.
   */
  private addWithSubscription<T>(
    task: Observable<T>,
    sub: Subject<T>,
    retriesRemaining: number,
    name?: string,
    replaceOld: boolean = false
  ): Observable<T> {
    if (replaceOld && !!name) {
      // allow to replace thus cancel older tasks of the same name;
      this.queue = this.queue.filter((queuedTask) => queuedTask.name !== name);
    }

    this.queue.push({
      task: task.pipe(
        tap(
          (res) => {
            sub.next(res);
            sub.complete();
          },
          (err) => {
            // If we're getting a (possible) concurrency-limit error, retry up to the specified number of times
            if (
              err.status === ERROR_CODE_EXCEEDED_CONCURRENCY && // Match the error code returned when we hit the concurrency limit
              (retriesRemaining || 0) > 0 && // Limit the number of retries to avoid over-exerting the server
              // Don't retry if there's already a newer version of this query queued
              !this.queue.find((queuedTask) => queuedTask.name === name)
            ) {
              this.addWithSubscription(task, sub, retriesRemaining - 1, name, false);
            } else {
              // Otherwise, notify the subscribers that the request has failed
              sub.error(err);
              sub.complete();
            }
          }
        )
      ),
      name,
      retriesRemaining
    });
    if (this.debug) {
      console.log('Queue - task added:', name, 'Now in queue:', this.queue.length);
    }
    // async to guarantee the return statement here executes before the queue fetching triggers
    setTimeout(() => this.fetchQueued(), 0);
    return sub.asObservable();
  }

  /**
   * Subscribes to one after another observable in the queue. Ideally those observables are notifying someone else upon completion
   * A possible pattern would be to put an observable in here that calls next
   * on a subject that was already provided to some outside subscriber
   */
  private fetchQueued() {
    if (this.queue.length && this.activeRequests < this.getConcurrentRequestLimit()) {
      this.activeRequests++;
      const next = this.queue.shift();
      if (this.debug) {
        console.log('Queue - next task started', next.name);
      }
      // convert to promise to avoid issues with completion/unsubscribe
      next.task
        .toPromise()
        .then(
          (res) => {
            if (this.debug) {
              console.log('Queue - task completed');
            }
          },
          (err) => {
            if (this.debug) {
              console.log('Queue - task failed');
            }
          }
        )
        .finally(() => {
          this.activeRequests--;
          this.fetchQueued();
        });
    } else if (this.queue.length) {
      if (this.debug) {
        console.log('Queue - busy');
      }
      // something left but queue busy, should resolve itself but just in case
      // poke the queue again in a few seconds... if it is empty then, this will be a NO-OP
      setTimeout(() => this.fetchQueued, 10000);
    }
  }

  /**
   * Get the number of concurrent requests that is allowed (the default is 3, but we reduce
   * this to 1 when we're sending computationally expensive requests)
   *
   * @returns Number of concurrent requests allowed
   */
  private getConcurrentRequestLimit(): number {
    const currentMetricSwitch = this.metricService.getMetricSwitch();
    const isAsync = ASYNC_METRIC_SWITCH_OPTIONS.indexOf(currentMetricSwitch) !== -1;
    return isAsync ? ASYNC_CONCURRENT_REQUEST_LIMIT : CONCURRENT_REQUEST_LIMIT;
  }
}
